<script setup lang="ts">
// Bottom-sheet variant of the place-bid form. Mounted at shell level
// (App.vue) so the sheet can slide up from wherever the bidder
// tapped -- no full-page navigation required.
//
// URL contract: `?bid=item:ITEMID` or `?bid=bundle:BUNDLEID` opens the
// sheet for that entity — a single combined query param disambiguates
// item vs. bundle rather than adding a second parallel param. Add
// `&max=1` to start in max-bid mode. Closing the sheet removes those
// query params (so refresh / back-nav land sensibly).
//
// Hoisted from features/bid/components/ to shell/components/ per MFE
// decomposability rules (App.vue imports it at shell level).
import { computed, watch, ref, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import SelectButton from 'primevue/selectbutton';
import Message from 'primevue/message';

import { useAuctionEvent, useWs, useSession, useTabletOrLarger } from '@/shell';
import { BidAmountInput, MaxBidExplanation, useBidStore, usePlaceBid } from '@/features/bid';
import type { BidTargetKind } from '@/features/bid';
// Shell-to-feature import — the kernel-allowed direction (same as
// BottomNav/SideNav's badge lookup). usePlaceBid lives in features/bid and
// can't reach into features/my-bids itself, so the shell-hoisted caller
// notes the placement here instead.
import { useMyBidsStore } from '@/features/my-bids';

const route = useRoute();
const router = useRouter();
const auctionEvent = useAuctionEvent();
const session = useSession();
const bid = useBidStore();
const myBids = useMyBidsStore();
const ws = useWs();
const { place, submitting } = usePlaceBid();

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const slug = computed(() => route.params.slug as string);

// `?bid` carries a combined `kind:id` token (e.g. `?bid=item:42` or
// `?bid=bundle:7`) so a single query param disambiguates item vs.
// bundle instead of adding a second parallel param. Bare numeric legacy
// values (e.g. `?bid=42`) are treated as `item:42` for backward compat
// with any stale deep links. Anything else (missing, malformed) means
// "sheet closed".
const queryTarget = computed<{ kind: BidTargetKind; id: number } | null>(() => {
  const raw = route.query.bid;
  if (typeof raw !== 'string') return null;
  const [head, tail] = raw.includes(':') ? raw.split(':') : ['item', raw];
  const kind: BidTargetKind = head === 'bundle' ? 'bundle' : 'item';
  const n = Number.parseInt(tail ?? '', 10);
  return Number.isFinite(n) && n > 0 ? { kind, id: n } : null;
});

const detail = computed(() => bid.detail);
const eventPaused = computed(() => auctionEvent.event?.isPaused === true);
// Admin-configurable per auction event -- when off, bidders may only
// place standard bids. Defaults open (true) so the sheet isn't blocked
// while the event is still loading.
const proxyBiddingEnabled = computed(() => auctionEvent.event?.proxyBidding ?? true);

// bid.viewerIsHighBidder is derived from a WS frame landing on this item's
// channel while something is subscribed to it — closing this sheet right
// after a successful placement unsubscribes before that frame's fan-out
// (SQS + worker) typically arrives, so it never flips true for a real
// placement. myBids.getBidState() is refreshed via a direct HTTP refetch
// (noteOwnPlacement) and stays correct regardless of WS timing or which
// surface (this sheet vs. InlineBidForm) is currently mounted, so OR it in
// rather than trusting the WS-derived flag alone.
const viewerIsLeading = computed(
  () =>
    bid.viewerIsHighBidder ||
    myBids.getBidState(
      bid.kind === 'item' ? bid.itemId : null,
      bid.kind === 'bundle' ? bid.itemId : null,
    ) === 'winning',
);

// GT1-395 — copy varies by whether proxy bidding is available and, when it
// is, whether the viewer already has a max bid on file. Proxy-off has no
// max-bid lever to point them at, so it just reassures them they'll hear
// about an outbid.
const highBidderHint = computed(() => {
  if (!viewerIsLeading.value) return null;
  if (!proxyBiddingEnabled.value) {
    return "You're the highest bidder. We'll notify you if you're outbid.";
  }
  return detail.value?.myMaxAmountCents != null
    ? "You're the highest bidder. Raise your max bid to stay ahead automatically."
    : "You're the highest bidder. Set a max bid to stay ahead automatically.";
});

// Leading on Standard means there is no valid action — overbidding
// yourself is pointless — so the amount input hides and the submit
// renders disabled as "Winning". With proxy bidding off the type is
// pinned to standard (watcher below), so the standard-only leading case
// collapses into the same state.
const leadingOnStandard = computed(() => viewerIsLeading.value && !bid.isMaxBid);

const open = ref(false);

// Bottom sheet is a mobile pattern — at tablet+ the same content opens as
// a right-side drawer (a full-width sheet sliding up over a desktop page
// reads as a blown-up phone UI). The drag handle is a bottom-sheet
// affordance and hides on the side variant.
const isTabletOrLarger = useTabletOrLarger();
const sheetPosition = computed(() => (isTabletOrLarger.value ? 'right' : 'bottom'));

// Keep the bid type from ever settling on 'max' while proxy bidding is
// off -- covers the sheet staying open across an item swap and any
// server-driven max-bid state (e.g. a stale 'max' from a prior item).
watch(
  proxyBiddingEnabled,
  (enabled) => {
    if (!enabled && bid.type === 'max') bid.setType('standard');
  },
  { immediate: true },
);

let loadSeq = 0;
watch(
  queryTarget,
  async (target) => {
    if (target == null) {
      open.value = false;
      return;
    }
    const { kind, id } = target;
    const bidParam = `${kind}:${id}`;
    // Guard deep-linked ?bid= URLs (e.g. from share links) when the
    // bidder is not signed in — close the sheet and route to sign-in.
    if (!session.isAuthenticated) {
      const currentPath = route.path;
      const { bid: _b, max: _m, ...restQuery } = route.query;
      void _b; void _m;
      void router.replace({ path: currentPath, query: restQuery });
      void router.push({
        path: `/${slug.value}/sign-in`,
        query: {
          item: String(id),
          next: `${currentPath}?bid=${bidParam}${route.query.max === '1' ? '&max=1' : ''}`,
        },
      });
      return;
    }
    const seq = ++loadSeq;
    await bid.loadDetail(slug.value, id, kind);
    if (seq !== loadSeq) return;
    // Land on max only when explicitly asked (?max=1 — the "Set max bid"
    // entry point). While leading, the sheet opens on Standard showing the
    // disabled "Winning" button; Max stays a deliberate switch, never an
    // automatic one.
    const wantsMax = route.query.max === '1';
    bid.setType(proxyBiddingEnabled.value && wantsMax ? 'max' : 'standard');
    open.value = true;
  },
  { immediate: true },
);

let sheetChannels: string[] = [];

function subscribeSheet(): void {
  if (sheetChannels.length > 0) {
    ws.unsubscribe(sheetChannels, bid.applyFrame);
    sheetChannels = [];
  }
  if (!open.value || !bid.itemId) return;
  const uuid = session.bidder?.uuid;
  const channels: string[] = [`${bid.kind}:${bid.itemId}`];
  if (uuid) channels.push(`bidder:${uuid}`);
  ws.subscribe(channels, bid.applyFrame);
  sheetChannels = channels;
}

watch(
  [open, () => bid.itemId] as const,
  ([isOpen]) => {
    if (!isOpen) {
      if (sheetChannels.length > 0) {
        ws.unsubscribe(sheetChannels, bid.applyFrame);
        sheetChannels = [];
      }
    } else {
      subscribeSheet();
    }
  },
);

onBeforeUnmount(() => {
  if (sheetChannels.length > 0) {
    ws.unsubscribe(sheetChannels, bid.applyFrame);
    sheetChannels = [];
  }
});

// While the viewer leads, BOTH toggle options stay selectable — the
// leading state lives on the submit button (disabled, "Winning"), not on
// the selector. Mirrors InlineBidForm's desktop treatment.
const typeOptions = computed(() => [
  { label: 'Standard bid', value: 'standard' as const },
  { label: 'Max bid', value: 'max' as const },
]);

const isStartingBid = computed(() => (detail.value?.bidCount ?? 0) === 0);
// While the viewer leads, the high bid IS their bid — same vocabulary as
// the item-detail bar and the My Bids cards.
const currentBidLabel = computed(() => {
  if (viewerIsLeading.value) return 'Your bid';
  return isStartingBid.value ? 'Starting bid' : 'Current bid';
});
const currentBidAmount = computed(() => {
  if (!detail.value) return '';
  const cents = isStartingBid.value
    ? detail.value.startingBidCents
    : detail.value.currentHighBidCents;
  return formatter.format(cents / 100);
});

// What the bidder will actually pay if their bid wins: the typed amount,
// falling back to the next minimum if nothing's been typed yet.
const placeBidAmount = computed(() => {
  if (!detail.value) return '';
  const fallback = isStartingBid.value
    ? detail.value.startingBidCents
    : detail.value.currentHighBidCents + detail.value.bidIncrementCents;
  const cents = bid.amountCents > 0 ? bid.amountCents : fallback;
  return formatter.format(cents / 100);
});

const submitLabel = computed(() => {
  if (submitting.value) return 'Placing...';
  // The leading state lives here by design: disabled button reading
  // "Winning" — switching the toggle to Max re-enables the max-bid flow.
  if (leadingOnStandard.value) return 'Winning';
  if (bid.isMaxBid) return `Set max bid · ${placeBidAmount.value}`;
  return `Place bid · ${placeBidAmount.value}`;
});

const photoUrl = computed(() => {
  const photos = detail.value?.photos ?? [];
  const real = photos.find(
    (p) => !(p.medium.includes('placehold') || p.medium.includes('placeholder')),
  );
  return real?.medium ?? null;
});

// Strip HTML to plain text for the sheet preview -- descriptionHtml comes
// from the trusted admin item editor (server sanitizes), but rendering it
// raw here would let images / headings break the sheet layout. We just
// need a short readable blurb.
const descriptionText = computed(() => {
  const html = detail.value?.descriptionHtml;
  if (!html) return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
});

function closeSheet(): void {
  const { bid: _b, max: _m, ...rest } = route.query;
  void _b;
  void _m;
  router.replace({ path: route.path, query: rest });
}

async function onSubmit(): Promise<void> {
  // Captured before the await: the bid store is shared with InlineBidForm,
  // and a route change while place() is in flight (e.g. a deep link opening
  // the sheet for a different entity) can repoint bid.kind / bid.itemId
  // before the placement resolves.
  const placementKind = bid.kind;
  const placementId = bid.itemId;
  const ok = await place();
  if (ok) {
    // Re-sync the Winning/Outbid buckets from the server instead of
    // depending on a single WS frame (stale-"Outbid"-after-rebid fix).
    myBids.noteOwnPlacement(
      placementKind === 'item' ? placementId : null,
      placementKind === 'bundle' ? placementId : null,
    );
    closeSheet();
  }
}

// Lock the shell body so dragging inside the sheet doesn't pan the
// catalog / item detail behind it.
interface ShellLockState {
  overflow: string;
  touchAction: string;
}
let lockState: ShellLockState | null = null;
let touchMoveBlocker: ((e: TouchEvent) => void) | null = null;

function setShellLock(locked: boolean): void {
  if (typeof document === 'undefined') return;
  const body = document.querySelector<HTMLElement>('.app-shell__body');
  if (!body) return;

  if (locked && !lockState) {
    lockState = {
      overflow: body.style.overflow,
      touchAction: body.style.touchAction,
    };
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';

    touchMoveBlocker = (e: TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('.p-drawer')) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', touchMoveBlocker, { passive: false });
  } else if (!locked && lockState) {
    body.style.overflow = lockState.overflow;
    body.style.touchAction = lockState.touchAction;
    lockState = null;
    if (touchMoveBlocker) {
      document.removeEventListener('touchmove', touchMoveBlocker);
      touchMoveBlocker = null;
    }
  }
}
watch(open, (isOpen) => setShellLock(isOpen));
onBeforeUnmount(() => setShellLock(false));
</script>

<template>
  <Drawer
    v-model:visible="open"
    :position="sheetPosition"
    :modal="true"
    :show-close-icon="false"
    :dismissable="true"
    :pt="{
      root: { class: 'bid-sheet' },
      mask: { class: 'bid-sheet__mask' },
      header: { class: 'bid-sheet__header-slot' },
      content: { class: 'bid-sheet__content' },
    }"
    @update:visible="(v) => { if (!v) closeSheet(); }"
  >
    <template #header>
      <div class="bid-sheet__header">
        <div
          v-if="!isTabletOrLarger"
          class="bid-sheet__handle"
          aria-hidden="true"
        />
        <Button
          icon="pi pi-times"
          text
          rounded
          aria-label="Close"
          class="bid-sheet__close"
          data-testid="place-bid__close"
          @click="closeSheet"
        />
      </div>
    </template>

    <article
      class="bid-sheet__body"
      data-testid="place-bid"
    >
      <Message
        v-if="bid.detailState === 'error'"
        severity="error"
        :closable="false"
      >
        We couldn't load this item. Try refreshing.
      </Message>

      <template v-else-if="detail">
        <div
          class="bid-sheet__hero"
          :class="{ 'bid-sheet__hero--empty': !photoUrl }"
        >
          <img
            v-if="photoUrl"
            :src="photoUrl"
            :alt="detail.title"
          >
          <i
            v-else
            class="pi pi-image bid-sheet__hero-icon"
            aria-hidden="true"
          />
        </div>

        <header class="bid-sheet__item-header">
          <h2
            class="bid-sheet__item-title"
            data-testid="place-bid__title"
          >
            {{ detail.title }}
          </h2>
          <p
            v-if="descriptionText"
            class="bid-sheet__description"
            data-testid="place-bid__description"
          >
            {{ descriptionText }}
          </p>
        </header>

        <hr class="bid-sheet__divider">

        <SelectButton
          v-if="proxyBiddingEnabled"
          :model-value="bid.type"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          option-disabled="disabled"
          :allow-empty="false"
          class="bid-sheet__type"
          data-testid="place-bid__type"
          @update:model-value="bid.setType($event)"
        />

        <Message
          v-if="bid.maxExceeded"
          severity="warn"
          :closable="false"
          data-testid="place-bid__max-exceeded"
        >
          Your max was exceeded. Current bid is {{ formatter.format((bid.detail?.currentHighBidCents ?? 0) / 100) }}. Set a new max to stay in the running.
        </Message>

        <Message
          v-if="highBidderHint"
          severity="info"
          :closable="false"
          data-testid="place-bid__highbidder-hint"
        >
          {{ highBidderHint }}
        </Message>

        <MaxBidExplanation v-else-if="bid.isMaxBid" />

        <!-- No valid amount exists while leading on Standard — the input
             hides and the disabled "Winning" button in the footer carries
             the state. -->
        <template v-if="!leadingOnStandard">
          <BidAmountInput />

          <Message
            v-if="bid.errorMessage"
            severity="warn"
            :closable="false"
            data-testid="place-bid__error"
          >
            {{ bid.errorMessage }}
          </Message>
        </template>
      </template>
    </article>

    <footer
      v-if="detail"
      class="bid-sheet__footer"
      data-testid="place-bid__footer"
    >
      <div class="bid-sheet__footer-meta">
        <span class="bid-sheet__footer-label">{{ currentBidLabel }}</span>
        <span class="bid-sheet__footer-value">{{ currentBidAmount }}</span>
      </div>
      <Button
        :label="submitLabel"
        :loading="submitting"
        :disabled="submitting || leadingOnStandard || eventPaused || detail.status !== 'open' || bid.amountCents <= 0 || bid.amountExceedsMax"
        class="bid-sheet__submit"
        data-testid="place-bid__submit"
        @click="onSubmit"
      />
    </footer>
  </Drawer>
</template>

<style scoped>
/* PrimeVue's `.p-drawer-bottom .p-drawer` rule has 2-class specificity
   and a fixed 10rem default -- match the same scope so our override wins
   without resorting to `!important`. */
:global(.p-drawer-bottom .p-drawer.bid-sheet) {
  height: 85vh;
  max-height: 85vh;
  border-top-left-radius: var(--app-radius-xl);
  border-top-right-radius: var(--app-radius-xl);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  display: flex;
  flex-direction: column;
}
/* Tablet+ side-drawer variant — full height, fixed width, square edges
   (matches the admin drawers rather than the phone bottom sheet). */
:global(.p-drawer-right .p-drawer.bid-sheet) {
  width: 440px;
  max-width: 100vw;
  height: 100%;
  border-radius: 0;
  display: flex;
  flex-direction: column;
}
/* Full-height drawer: the body grows so the footer pins to the bottom
   edge (drawer convention), instead of floating after the content. */
:global(.p-drawer-right .p-drawer.bid-sheet .bid-sheet__body) {
  flex: 1;
}
:global(.bid-sheet__header-slot) {
  padding: 0 !important;
}
:global(.bid-sheet__content) {
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.bid-sheet__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding: var(--app-space-16) var(--app-space-12) var(--app-space-8);
  flex-shrink: 0;
}
.bid-sheet__handle {
  position: absolute;
  left: 50%;
  top: var(--app-space-8);
  transform: translateX(-50%);
  width: 40px;
  height: 4px;
  background: var(--app-border-muted);
  border-radius: 999px;
}
.bid-sheet__close {
  all: unset;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  font-size: var(--app-font-size-body-lg);
  cursor: pointer;
}
.bid-sheet__close:hover {
  background: var(--app-background-ghost);
  color: var(--app-text-primary);
}
.bid-sheet__close:focus-visible {
  outline: 2px solid var(--app-border-hover);
  outline-offset: 2px;
}
.bid-sheet__body {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-16);
  padding: 0 0 var(--app-space-20);
  overflow-y: auto;
  overscroll-behavior: contain;
}
.bid-sheet__hero {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: var(--app-background-ghost);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.bid-sheet__hero img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.bid-sheet__hero-icon {
  font-size: 48px;
  color: var(--app-border-muted);
}
.bid-sheet__item-header {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-8);
  padding: 0 var(--app-space-20);
}
.bid-sheet__item-title {
  margin: 0;
  font-size: var(--app-font-size-h2);
  line-height: var(--app-line-height-h2);
  font-weight: var(--app-font-weight-semibold);
  color: var(--app-text-primary);
}
.bid-sheet__description {
  margin: 0;
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  color: var(--app-text-muted);
}
.bid-sheet__divider {
  border: 0;
  border-top: var(--app-border-width-default) solid var(--app-border-muted);
  margin: var(--app-space-4) var(--app-space-20) 0;
}
.bid-sheet__body :deep(.p-message),
.bid-sheet__body > .bid-sheet__type,
.bid-sheet__body > [data-testid="bid-amount-input"],
.bid-sheet__body :deep(.app-field-group),
.bid-sheet__body :deep(.max-bid-explanation) {
  margin-left: var(--app-space-20);
  margin-right: var(--app-space-20);
}
.bid-sheet__type {
  align-self: flex-start;
}
.bid-sheet__footer {
  display: flex;
  align-items: center;
  gap: var(--app-space-12);
  padding: var(--app-space-12) var(--app-space-20);
  padding-bottom: calc(var(--app-space-12) + env(safe-area-inset-bottom));
  border-top: var(--app-border-width-default) solid var(--app-border-muted);
  background: var(--app-background-primary);
}
.bid-sheet__footer-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.bid-sheet__footer-label {
  font-size: var(--app-font-size-caption);
  line-height: var(--app-line-height-caption);
  color: var(--app-text-muted);
}
.bid-sheet__footer-value {
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  font-weight: var(--app-font-weight-semibold);
  color: var(--app-text-primary);
}
.bid-sheet__submit {
  flex: 1;
}
.bid-sheet__submit :deep(.p-button) {
  width: 100%;
  height: 56px;
  font-size: var(--app-font-size-body-base);
  font-weight: var(--app-font-weight-semibold);
}
</style>
